import type { HttpClient } from '../http/HttpClient'
import { breakdownLabel, isQuotable, isTree, leafLines } from '@/domain/quotation/quotationBreakdown'
import type { GroupMemberQuotationData } from '@/domain/requestGroup/GroupQuotation'
import type { Budget } from '@/types/budgets/budget'
import type { EvaluationCriterion } from '@/types/reception'
import { backendStatus } from '../http/errorDetail'
import type {
  GroupMemberDetailRepository,
  MemberActivity,
  MemberDispatchOrder,
  MemberReceptionForm,
  MemberPayload,
  MemberView,
  MemberDocument,
  MemberFlowNode,
  MemberLine,
  MemberTask,
  MemberUser,
  SplitsForm,
} from '@/domain/requestGroup/groupMemberDetail'
import type { SplitPayload } from '@/domain/requestGroup/groupAccounting'
import type { Account, CostCenter, SplittableCustomField } from '@/types/split'

interface RawUser {
  id?: number | null
  name?: string | null
  profile_picture?: string | null
  area?: { name?: string | null } | string | null
}

interface RawNode {
  uuid: string
  type: string
  title?: string | null
  display_title?: string | null
  status?: string | null
  date?: string | null
  user?: RawUser | null
  is_current_node?: boolean
}

interface RawTimeline {
  number?: number | null
  request_type: string
  status?: { status: string; name?: string | null; color?: string | null } | null
  description?: string | null
  policy?: { name?: string | null; display_name?: string | null } | null
  area?: string | null
  category?: string | null
  subcategory?: string | null
  request_date?: string | null
  limit_date?: string | null
  requester?: RawUser | null
  followers?: RawUser[] | null
  is_follower?: boolean
  is_requester?: boolean
  budget?: number | null
  budget_currency_code?: string | null
  total?: number | null
  totals?: { currency?: string | null; total?: number | null }[] | null
  approvals_graph?: { nodes?: RawNode[]; edges?: { source: string; target: string; is_loopback?: boolean }[] } | null
  tasks?: {
    node_id?: number | null
    type: string
    name?: string | null
    approval_text?: string | null
    can_reject?: boolean
    subtasks?: { title?: string | null; assignee?: RawUser | null; is_assignee?: boolean }[]
  }[] | null
  request_lines?: {
    id: number
    product?: string | null
    label?: string | null
    description?: string | null
    quantity?: number | string | null
    net_total?: number | string | null
    total?: number | string | null
    currency?: string | { code?: string | null } | null
    status?: string | null
    parent_id?: number | null
    is_leaf?: boolean
    breakdown_values?: Record<string, unknown> | null
  }[] | null
  documents?: { name: string; url: string; type?: string | null; uploader?: string | null; blocked?: boolean }[] | null
  activity?: {
    title?: string | null
    text?: string | null
    date: string
    user?: string | null
    profile_picture?: string | null
    status?: string | null
  }[] | null
}

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function mapUser(raw: RawUser | null | undefined): MemberUser | null {
  if (!raw?.name) return null
  const area = typeof raw.area === 'string' ? raw.area : (raw.area?.name ?? null)
  return { id: raw.id ?? null, name: raw.name, avatar: raw.profile_picture ?? null, area }
}

function mapCurrency(value: string | { code?: string | null } | null | undefined): string | null {
  if (!value) return null
  return typeof value === 'string' ? value : (value.code ?? null)
}

/** Exported for the mapper test: the timeline is the one payload the normal request detail is drawn from. */
export function mapMemberView(requestId: number, raw: RawTimeline): MemberView {
  const nodes: MemberFlowNode[] = (raw.approvals_graph?.nodes ?? []).map((node) => ({
    uuid: node.uuid,
    type: node.type,
    title: node.display_title ?? node.title ?? null,
    status: node.status ?? '',
    date: node.date ?? null,
    user: mapUser(node.user),
    isCurrent: node.is_current_node === true,
  }))
  const tasks: MemberTask[] = (raw.tasks ?? []).map((task) => ({
    nodeId: task.node_id ?? null,
    type: task.type,
    name: task.name ?? task.type,
    approvalText: task.approval_text ?? '',
    canReject: task.can_reject === true,
    subtasks: (task.subtasks ?? []).map((sub) => ({
      title: sub.title ?? '',
      assigneeName: sub.assignee?.name ?? null,
      isAssignee: sub.is_assignee === true,
    })),
  }))
  const lines: MemberLine[] = (raw.request_lines ?? []).map((line) => ({
    id: line.id,
    product: line.product ?? line.label ?? '',
    description: line.description ?? null,
    quantity: toNumber(line.quantity),
    netTotal: toNumber(line.net_total),
    total: toNumber(line.total),
    currency: mapCurrency(line.currency),
    status: line.status ?? '',
    parentId: line.parent_id ?? null,
    isLeaf: line.is_leaf !== false,
    breakdownLabel: line.parent_id ? breakdownLabel(line.breakdown_values) || null : null,
  }))
  const documents: MemberDocument[] = (raw.documents ?? []).map((doc) => ({
    name: doc.name,
    url: doc.url,
    type: doc.type ?? null,
    uploader: doc.uploader ?? null,
    blocked: doc.blocked === true,
  }))
  const activity: MemberActivity[] = (raw.activity ?? []).map((entry) => ({
    title: entry.title ?? '',
    text: entry.text ?? null,
    date: entry.date,
    user: entry.user ?? null,
    avatar: entry.profile_picture ?? null,
    status: entry.status ?? '',
  }))
  return {
    requestId,
    number: raw.number ?? null,
    requestType: raw.request_type,
    status: { code: raw.status?.status ?? '', name: raw.status?.name ?? null, color: raw.status?.color ?? null },
    description: raw.description ?? null,
    policyName: raw.policy?.display_name ?? raw.policy?.name ?? null,
    area: raw.area ?? null,
    category: raw.category ?? null,
    subcategory: raw.subcategory ?? null,
    requestDate: raw.request_date ?? null,
    limitDate: raw.limit_date ?? null,
    requester: mapUser(raw.requester),
    followers: (raw.followers ?? []).map(mapUser).filter((user): user is MemberUser => user !== null),
    isFollower: raw.is_follower === true,
    isRequester: raw.is_requester === true,
    budget: toNumber(raw.budget),
    budgetCurrency: raw.budget_currency_code ?? null,
    total: toNumber(raw.total),
    totals: (raw.totals ?? []).flatMap((row) => {
      const total = toNumber(row.total)
      return total === null ? [] : [{ currency: row.currency ?? null, total }]
    }),
    flow: {
      nodes,
      edges: (raw.approvals_graph?.edges ?? []).map((edge) => ({
        source: edge.source,
        target: edge.target,
        isLoopback: edge.is_loopback === true,
      })),
    },
    tasks,
    lines,
    documents,
    activity,
  }
}

interface RawLine {
  id: number
  item?: { name?: string } | null
  item_description?: string | null
  description?: string | null
  quantity?: number | null
  parent_id?: number | null
  is_leaf?: boolean
  breakdown_values?: Record<string, unknown> | null
}

export interface RawMemberRequest {
  line_structure?: string
  items: RawLine[]
  providers: { id: number; name: string; has_email?: boolean }[]
  min_quotations?: number | null
  quotations_answered_count?: number | null
}

const lineName = (line: RawLine) => line.item?.name || line.item_description || line.description || ''

const lineText = (line: RawLine) => (line.item_description || line.description || '').trim()

/** A parent row is a heading and a child left at zero is never sent out: neither is offered to quote. */
export function toMemberQuotationData(requestId: number, raw: RawMemberRequest): GroupMemberQuotationData {
  const tree = isTree(raw.line_structure)
  const groups = new Map(raw.items.filter((line) => line.is_leaf === false).map((line) => [line.id, line]))
  const candidates = tree ? leafLines(raw.items).filter((line) => isQuotable({ quantity: line.quantity })) : raw.items

  return {
    requestId,
    lineStructure: tree ? 'TREE' : 'FLAT',
    lines: candidates.map((line) => {
      const label = tree ? breakdownLabel(line.breakdown_values) || lineName(line) : lineName(line)
      const group = tree && line.parent_id ? groups.get(line.parent_id) : undefined
      const groupLabel = group ? lineName(group) : null
      // why: two lines of one product read the same by name; the activity text is what tells them apart.
      const text = lineText(group ?? line)
      return {
        id: line.id,
        label,
        groupLabel,
        detail: text && text !== label && text !== groupLabel ? text : null,
        description: line.description || label,
        quantity: Number(line.quantity ?? 0),
      }
    }),
    providers: raw.providers.map((provider) => ({ id: provider.id, name: provider.name, hasEmail: provider.has_email !== false })),
    minQuotations: raw.min_quotations ?? 0,
    answeredCount: raw.quotations_answered_count ?? 0,
  }
}

export class HttpGroupMemberDetailRepository implements GroupMemberDetailRepository {
  constructor(private http: HttpClient) {}

  async getDetail(requestId: number): Promise<MemberPayload> {
    const raw = await this.http
      .url('purchases/requests/timeline')
      .params({ request_id: String(requestId) })
      .get<RawTimeline>()
    return { raw: raw as unknown as Record<string, unknown>, view: mapMemberView(requestId, raw) }
  }

  async getQuotationData(requestId: number): Promise<GroupMemberQuotationData> {
    const raw = await this.http
      .url(`purchases/requests/${requestId}`)
      .params({ recipient_type: 'PROVIDER' })
      .get<RawMemberRequest>()
    return toMemberQuotationData(requestId, raw)
  }

  async setFollowing(requestId: number, currentlyFollowing: boolean): Promise<void> {
    await this.http
      .url(`purchases/requests/${requestId}/followers`)
      .body({ isFollower: currentlyFollowing })
      .put()
  }

  async addComment(requestId: number, comment: string, taggedUsers: number[] = []): Promise<void> {
    await this.http
      .url('purchases/comments')
      .body({ comment, process: 'request', pid: String(requestId), taggedUsers })
      .post()
  }

  async skipTechnicalEvaluation(requestId: number, nodeId: number | null, comments: string): Promise<void> {
    await this.http
      .url(`purchases/requests/${requestId}/actions`)
      .body({ action: 'SKIP_TECHNICAL_EVALUATION', comments, ...(nodeId === null ? {} : { node_id: nodeId }) })
      .post()
  }

  async answerTask(requestId: number, body: object | FormData): Promise<void> {
    await this.http.url(`purchases/requests/${requestId}`).body(body).put()
  }

  async setPurchaseOrderDispatch(purchaseOrderId: number, send: boolean): Promise<void> {
    await this.http
      .url(`purchases/purchase_orders/${purchaseOrderId}/${send ? 'dispatch' : 'skip_dispatch'}`)
      .body(send ? {} : { comment: '' })
      .post()
  }

  async overrideMinQuotations(requestId: number, nodeId: number | null, justification: string): Promise<void> {
    await this.http
      .url(`purchases/requests/${requestId}/min-quotations-override`)
      .body({ justification, ...(nodeId === null ? {} : { node_id: nodeId }) })
      .post()
  }

  async getAffectedBudgets(requestId: number): Promise<Budget[]> {
    try {
      const raw = await this.http.url(`budgets/affected-by-request/${requestId}`).get<{ results: Budget[] }>()
      return raw.results
    } catch (error) {
      // why: the endpoint answers 404 when the society has no budget for the request's date: nothing to review, not a failure.
      if (backendStatus(error) === 404) return []
      throw error
    }
  }

  async getDispatchOrders(requestId: number, nodeId: number | null): Promise<MemberDispatchOrder[]> {
    const raw = await this.http
      .url(`purchases/requests/${requestId}/dispatch_data`)
      .params({ node: String(nodeId ?? 0) })
      .get<{ pos: MemberDispatchOrder[] }>()
    return raw.pos
  }

  async getReceptionForm(requestId: number, providerId: number): Promise<MemberReceptionForm> {
    return this.http
      .url('purchases/receptions/form')
      .params({ provider_filter: String(providerId), request_id: String(requestId) })
      .get<MemberReceptionForm>()
  }

  async getReceptionCriteria(): Promise<EvaluationCriterion[]> {
    const raw = await this.http.url('providers/evaluation-config').get<{ enabled?: boolean; criteria?: EvaluationCriterion[] }>()
    return raw.enabled ? raw.criteria ?? [] : []
  }

  async getSplitsForm(): Promise<SplitsForm> {
    const raw = await this.http
      .url('purchases/splits/form')
      .get<{ cost_centers: CostCenter[]; accounts: Account[]; splittable_custom_fields?: SplittableCustomField[] | null }>()
    return { costCenters: raw.cost_centers, accounts: raw.accounts, customFields: raw.splittable_custom_fields ?? [] }
  }

  async saveAccountingData(requestId: number, splits: SplitPayload[]): Promise<void> {
    await this.http.url(`purchases/requests/${requestId}/accounting_data`).body({ splits }).post()
  }

  async uploadDocument(requestId: number, file: File): Promise<void> {
    const body = new FormData()
    body.append('document', file)
    await this.http.url(`purchases/requests/${requestId}/documents`).body(body).post()
  }

  async deleteDocument(requestId: number, name: string): Promise<void> {
    await this.http.url(`purchases/requests/${requestId}/documents`).body({ name }).put()
  }
}
